import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getConnectionState, onConnectionChange, onUnauthorized } from './api';
import {
  adoptUiScale,
  coerceUiScale,
  preferenceQueryKey,
  PREFERENCE_STALE_TIME_MS,
} from './uiScale';
import { sessionGate, unauthorizedOutcome } from './sessionGate';
import { adminRouteGate } from './roleGate';
import { ChangePasswordDialog } from '@/components/ChangePasswordDialog';
import { SessionExpiredDialog } from '@/components/SessionExpiredDialog';
import type { AuthState, PublicUser } from '@shared/schemas.js';

/**
 * Authentication state for the client.
 *
 * `authEnabled` comes from the server, and while it is false an anonymous user
 * is a completely normal state. Nothing here redirects anyone on its own; the
 * decision of what to render lives in the pure `sessionGate` so it can be
 * tested without a browser.
 */

interface AuthContextValue {
  /** Whether the server enforces authentication. */
  authEnabled: boolean;
  user: PublicUser | null;
  isLoading: boolean;
  /**
   * A 401 arrived while this person was signed in and working.
   *
   * The distinguishing fact is that they had a session. Their screen is still
   * on display, possibly with half-typed call notes in it, so this drives an
   * in-place password prompt rather than a navigation.
   */
  sessionExpired: boolean;
  login: (username: string, password: string) => Promise<PublicUser>;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  refresh: () => Promise<unknown>;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

const ANONYMOUS: AuthState = { authEnabled: false, user: null };

export function AuthProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const queryClient = useQueryClient();
  const [sessionExpired, setSessionExpired] = React.useState(false);

  const state = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
    // A failure here must not blank the application while auth is off.
    retry: false,
    staleTime: 60_000,
  });

  /**
   * The server says a session is gone.
   *
   * What follows depends entirely on whether there WAS one. The cached
   * auth state is read at call time rather than captured in a closure, so this
   * effect subscribes once and still sees current values.
   *
   * Note what is deliberately absent: the cached user is NOT cleared. Clearing
   * it is what made the route unmount and took the VA's unsaved notes with it.
   * The user object is a UI convenience; the server is the only authority on
   * what this person may actually do, and it is already refusing them.
   */
  React.useEffect(
    () =>
      onUnauthorized(() => {
        const cached = queryClient.getQueryData<AuthState>(['auth', 'me']);
        const outcome = unauthorizedOutcome({
          authEnabled: cached?.authEnabled ?? false,
          hasUser: !!cached?.user,
        });
        if (outcome === 'expire') setSessionExpired(true);
      }),
    [queryClient],
  );

  const resolved = state.data ?? ANONYMOUS;

  useServerUiScale(resolved.user);

  const value = React.useMemo<AuthContextValue>(
    () => ({
      authEnabled: resolved.authEnabled,
      user: resolved.user,
      isLoading: state.isLoading,
      sessionExpired,
      login: async (username, password) => {
        const { user } = await api.login(username, password);
        // Drop every cached preference before the new user's query can run.
        // Whoever was signed in before has no bearing on this account, and a
        // leftover entry would flash their scale on the first render.
        queryClient.removeQueries({ queryKey: ['auth', 'preferences'] });
        queryClient.setQueryData(['auth', 'me'], { authEnabled: resolved.authEnabled, user });
        // The session is live again; the dialog closes and the route underneath
        // it — which never unmounted — becomes usable exactly where it was.
        setSessionExpired(false);
        // Anything cached anonymously may look different once signed in.
        await queryClient.invalidateQueries();
        return user;
      },
      logout: async () => {
        await api.logout();
        // The scale on screen deliberately survives logout — the login page
        // stays at the size the person was using — but the CACHED SERVER VALUE
        // must not, or signing back in would reuse it instead of asking.
        queryClient.removeQueries({ queryKey: ['auth', 'preferences'] });
        // A deliberate departure. There is no screen worth preserving, so this
        // clears the user and lets the gate redirect normally.
        setSessionExpired(false);
        queryClient.setQueryData(['auth', 'me'], { authEnabled: resolved.authEnabled, user: null });
        await queryClient.invalidateQueries();
      },
      changePassword: async (currentPassword, newPassword) => {
        const { user } = await api.changePassword(currentPassword, newPassword);
        queryClient.setQueryData(['auth', 'me'], { authEnabled: resolved.authEnabled, user });
      },
      refresh: () => queryClient.invalidateQueries({ queryKey: ['auth', 'me'] }),
    }),
    [queryClient, resolved.authEnabled, resolved.user, state.isLoading, sessionExpired],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = React.useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}

/**
 * Keep the interface scale following whoever is signed in.
 *
 * The rule, in order of authority:
 *
 *   1. **A signed-in user's server preference wins.** It is fetched as soon as
 *      there is a user and applied over whatever the local mirror said. This is
 *      what makes the admin's 125% and the VA's 140% independent on one machine
 *      — signing in as either restores their own.
 *   2. **Otherwise the local mirror stands.** Before login, and in anonymous
 *      mode while enforcement is off, there is no server preference to fetch
 *      and nothing to override the value main.tsx already applied.
 *
 * Logout deliberately does NOT reset anything: the login page stays at the
 * scale the person was just using, and the next account's preference replaces
 * it when they sign in.
 */
function useServerUiScale(user: PublicUser | null): void {
  const preferences = useQuery({
    queryKey: preferenceQueryKey(user?.id),
    queryFn: () => api.myPreferences(),
    enabled: !!user,
    // Stale immediately, so becoming authenticated always produces a fresh
    // read. See the note on PREFERENCE_STALE_TIME_MS for why five minutes was
    // wrong, and why this does not cause polling.
    staleTime: PREFERENCE_STALE_TIME_MS,
    retry: false,
  });

  const serverScale = preferences.data?.uiScale;
  React.useEffect(() => {
    if (serverScale === undefined) return;
    // adopt, not apply: the mirror is updated too, so a reload or a sign-out
    // keeps showing the same size rather than snapping back.
    adoptUiScale(coerceUiScale(serverScale));
  }, [serverScale]);
}

/** Subscribe to server reachability. Shared by the gate and the banner. */
export function useConnectionState(): 'ok' | 'offline' {
  const [state, setState] = React.useState(getConnectionState);
  React.useEffect(() => onConnectionChange(setState), []);
  return state;
}

/**
 * Gate the application — but only when the server says authentication is
 * enforced. While `auth.enabled` is false this renders its children for
 * everyone, which is what keeps LeadForge working exactly as it does today.
 *
 * Every branch here comes from `sessionGate`; this component only turns a
 * decision into elements.
 */
export function RequireAuth({ children }: { children: React.ReactNode }): JSX.Element {
  const { authEnabled, user, isLoading, sessionExpired } = useAuth();
  const connection = useConnectionState();

  const decision = sessionGate({
    authEnabled,
    user,
    sessionExpired,
    offline: connection === 'offline',
    isLoading,
  });

  if (decision === 'loading') {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (decision === 'redirect') return <LoginRedirect />;

  // A forced password change blocks the application, because the server blocks
  // it too: every non-account route returns 403 until it is resolved.
  if (decision === 'change-password') return <ForcedPasswordChange />;

  return (
    <>
      {children}
      {/*
        Rendered as a SIBLING of the route, never in place of it. That is the
        entire fix: the route keeps its React state, its form values and its
        scroll position while the password is re-entered on top.
      */}
      {decision === 'reauthenticate' && <SessionExpiredDialog />}
    </>
  );
}

/**
 * UX gate for routes whose server APIs are administrator-only.
 *
 * This does not replace backend authorization. It prevents a VA from manually
 * navigating to an admin URL and seeing a half-rendered page before its API
 * calls are rejected. When auth is disabled, legacy local-only behavior is
 * preserved and the route renders normally.
 */
export function RequireAdmin(): JSX.Element {
  const { authEnabled, user } = useAuth();
  const decision = adminRouteGate({ authEnabled, user });
  if (decision === 'redirect') return <Navigate to="/queue" replace />;
  return <Outlet />;
}

/**
 * The only screen a must-change-password user can reach. Deliberately has no
 * navigation: the alternative is a shell whose every link 403s.
 */
function ForcedPasswordChange(): JSX.Element {
  const { logout } = useAuth();
  const [open, setOpen] = React.useState(true);
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
      <p className="text-sm font-semibold">Choose a new password</p>
      <p className="max-w-sm text-xs text-muted-foreground">
        Your account was created or reset with a temporary password. Set your own before continuing.
      </p>
      <div className="flex gap-2">
        <button className="rounded border border-border px-3 py-1.5 text-xs" onClick={() => setOpen(true)}>
          Change password
        </button>
        <button className="rounded px-3 py-1.5 text-xs text-muted-foreground" onClick={() => void logout()}>
          Sign out
        </button>
      </div>
      <ChangePasswordDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}

/** Separate component so the import stays lazy-free and testable. */
function LoginRedirect(): JSX.Element {
  React.useEffect(() => {
    if (window.location.pathname !== '/login') {
      window.location.replace('/login');
    }
  }, []);
  return <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Redirecting…</div>;
}
